import { applyMove } from "@dailywars/core/engine";
import type { GameState, Move } from "@dailywars/core/engine";

import { interpretEvents } from "#/game/animation/fromEvents";
import { FAST_PLAYBACK_SPEED_MULTIPLIER } from "#/game/constants";
import type { AiPlaybackState, AiPlaybackStep, StoreState } from "#/game/store/types";

function scaleMoveAnimationDuration<T extends { durationMs: number }>(
  animation: T,
  ratio: number,
): T {
  if (ratio === 1) return animation;
  return { ...animation, durationMs: Math.max(1, Math.round(animation.durationMs / ratio)) };
}

function scaleStepMoveAnimations(steps: AiPlaybackStep[], ratio: number): AiPlaybackStep[] {
  if (ratio === 1) return steps;
  return steps.map((step) => ({
    ...step,
    animations: step.animations.map((animation) =>
      animation.type === "move" ? scaleMoveAnimationDuration(animation, ratio) : animation,
    ),
  }));
}

function playbackSpeedRatio(fastPlayback: boolean): number {
  return fastPlayback ? FAST_PLAYBACK_SPEED_MULTIPLIER : 1;
}

function playbackStepsForState({
  state,
  startState,
  moves,
}: {
  state: StoreState;
  startState: GameState;
  moves: Move[];
}): AiPlaybackStep[] {
  const steps = precomputePlaybackSteps(startState, moves);
  return scaleStepMoveAnimations(steps, playbackSpeedRatio(state.fastPlayback));
}

export function setFastPlayback({
  state,
  fastPlayback,
}: {
  state: StoreState;
  fastPlayback: boolean;
}): StoreState {
  if (state.fastPlayback === fastPlayback) return state;

  const ratio = playbackSpeedRatio(fastPlayback) / playbackSpeedRatio(state.fastPlayback);
  let next: StoreState = { ...state, fastPlayback };

  if (state.aiPlayback) {
    const index = state.aiPlayback.index;
    const steps = state.aiPlayback.steps.map((step, stepIndex) =>
      stepIndex >= index ? scaleStepMoveAnimations([step], ratio)[0]! : step,
    );
    next = { ...next, aiPlayback: { ...state.aiPlayback, steps } };
  }

  if (state.animation?.type === "move") {
    next.animation = scaleMoveAnimationDuration(state.animation, ratio);
  }

  if (state.animationQueue.length > 0) {
    next.animationQueue = state.animationQueue.map((animation) =>
      animation.type === "move" ? scaleMoveAnimationDuration(animation, ratio) : animation,
    );
  }

  return next;
}

type PlaybackDayDirection = "previous" | "next";

function replayDayStart({
  state,
  day,
}: {
  state: StoreState;
  day: number;
}): { state: GameState; playbackIndex: number } | null {
  if (state.serverState.currentDay === day && state.serverState.currentPlayer === "p1") {
    return { state: state.serverState, playbackIndex: 0 };
  }

  const playback = state.aiPlayback;
  if (!playback) return null;

  const stepIndex = playback.steps.findIndex(
    (step) => step.stateAfter.currentDay === day && step.stateAfter.currentPlayer === "p1",
  );
  if (stepIndex === -1) return null;

  return { state: playback.steps[stepIndex]!.stateAfter, playbackIndex: stepIndex + 1 };
}

function replayTargetDay({
  state,
  direction,
}: {
  state: StoreState;
  direction: PlaybackDayDirection;
}): number {
  if (direction === "next") return state.localState.currentDay + 1;

  const currentDayStart = replayDayStart({ state, day: state.localState.currentDay });
  const isAtCurrentDayStart = currentDayStart?.playbackIndex === state.aiPlayback?.index;
  return state.localState.currentDay - (isAtCurrentDayStart ? 1 : 0);
}

export function canSeekReplayDay({
  state,
  direction,
}: {
  state: StoreState;
  direction: PlaybackDayDirection;
}): boolean {
  return replayDayStart({ state, day: replayTargetDay({ state, direction }) }) !== null;
}

export function seekReplayDay({
  state,
  direction,
}: {
  state: StoreState;
  direction: PlaybackDayDirection;
}): StoreState {
  const target = replayDayStart({ state, day: replayTargetDay({ state, direction }) });
  if (!target || !state.aiPlayback) return state;

  return {
    ...state,
    localState: target.state,
    selection: { mode: "none" },
    animation: null,
    animationQueue: [],
    previewAnimation: null,
    hoveredTile: null,
    scrollFocusTile: null,
    aiPlayback: { ...state.aiPlayback, index: target.playbackIndex },
  };
}

function finalizePlayback(state: StoreState, playback: AiPlaybackState): StoreState {
  return {
    ...state,
    serverState: playback.finalState,
    serverDayNumber: playback.finalServerDayNumber,
    localState: playback.finalState,
    pendingMoves: [],
    selection: { mode: "none" },
    animation: null,
    animationQueue: [],
    isSubmitting: false,
    aiPlayback: null,
  };
}

export function precomputePlaybackSteps(startState: GameState, moves: Move[]): AiPlaybackStep[] {
  const steps: AiPlaybackStep[] = [];
  let current = startState;
  for (const move of moves) {
    const result = applyMove(current, move);
    const animations = interpretEvents({
      beforeState: current,
      events: result.events,
      afterState: result.state,
    });
    const unitId = "unitId" in move ? move.unitId : null;
    steps.push({ animations, stateAfter: result.state, unitId });
    current = result.state;
  }
  return steps;
}

export function playNextStep({
  state,
  playback,
}: {
  state: StoreState;
  playback: AiPlaybackState;
}): StoreState {
  if (playback.index >= playback.steps.length) {
    return finalizePlayback(state, playback);
  }
  const step = playback.steps[playback.index]!;
  const nextPlayback: AiPlaybackState = { ...playback, index: playback.index + 1 };
  const base: StoreState = {
    ...state,
    localState: step.stateAfter,
    selection: { mode: "none" },
    isSubmitting: false,
    aiPlayback: nextPlayback,
  };

  if (step.animations.length === 0) {
    return { ...base, animation: null, animationQueue: [] };
  }

  const nowMs = typeof performance !== "undefined" ? performance.now() : 0;
  const [first, ...rest] = step.animations;
  return {
    ...base,
    animation: { ...first!, startedAtMs: nowMs },
    animationQueue: rest.map((a) => ({ ...a, startedAtMs: 0 })),
  };
}

export function beginPlaybackAfterSubmit({
  state,
  finalState,
  aiMoves,
  finalServerDayNumber,
  includePlayerEndTurnPlayback = true,
}: {
  state: StoreState;
  finalState: GameState;
  aiMoves: Move[];
  finalServerDayNumber: number;
  includePlayerEndTurnPlayback?: boolean;
}): StoreState {
  const moves = includePlayerEndTurnPlayback
    ? ([{ type: "endTurn" }, ...aiMoves] as Move[])
    : aiMoves;
  const steps = playbackStepsForState({ state, startState: state.localState, moves });
  const playback: AiPlaybackState = { steps, index: 0, finalState, finalServerDayNumber };
  if (steps.length === 0) {
    return finalizePlayback(state, playback);
  }
  return playNextStep({ state, playback });
}

export function beginReplayPlayback({
  state,
  initialState,
  moves,
  finalServerDayNumber,
}: {
  state: StoreState;
  initialState: GameState;
  moves: Move[];
  finalServerDayNumber: number;
}): StoreState {
  const steps = playbackStepsForState({ state, startState: initialState, moves });
  const finalState = steps.length > 0 ? steps[steps.length - 1]!.stateAfter : initialState;
  const playback: AiPlaybackState = { steps, index: 0, finalState, finalServerDayNumber };
  const seeded: StoreState = {
    ...state,
    serverState: initialState,
    localState: initialState,
    pendingMoves: [],
    selection: { mode: "none" },
    animation: null,
    animationQueue: [],
    isSubmitting: false,
    aiPlayback: null,
  };
  if (steps.length === 0) {
    return finalizePlayback(seeded, playback);
  }
  return playNextStep({ state: seeded, playback });
}
